import {
  ESSAY_SHRINKOPEDIA_LINKS,
  CREDENTIALING_SHRINKOPEDIA_LINKS,
  shrinkopediaUrl,
} from "@/data/shrinkopediaLinks";

interface AlsoOnShrinkopediaProps {
  /** Perspectives essay slug OR credentialing page slug. */
  slug: string;
  /**
   * Which map to look up in.
   *   "essay"          → uses ESSAY_SHRINKOPEDIA_LINKS + campaign auto_perspectives_{slug}
   *   "credentialing"  → uses CREDENTIALING_SHRINKOPEDIA_LINKS + campaign auto_credentialing_{slug}
   */
  variant?: "essay" | "credentialing";
  className?: string;
}

/**
 * "Also on Shrinkopedia" cross-reference block. First-person voice framing
 * reflects that Shrinkopedia is the encyclopedia Dr. Refai medically reviews
 * and this essay is him speaking as himself.
 *
 * Renders nothing (no wrapper, no heading) when no matching entries exist —
 * this keeps essays like /digital-mental-health-needs-more-than-convenience
 * clean when Shrinkopedia does not yet have a reference entry for the topic.
 */
export function AlsoOnShrinkopedia({
  slug,
  variant = "essay",
  className,
}: AlsoOnShrinkopediaProps) {
  const registry =
    variant === "credentialing"
      ? CREDENTIALING_SHRINKOPEDIA_LINKS
      : ESSAY_SHRINKOPEDIA_LINKS;
  const entries = registry[slug];
  if (!entries || entries.length === 0) return null;

  const campaign =
    variant === "credentialing"
      ? `auto_credentialing_${slug.replace(/-/g, "_")}`
      : `auto_perspectives_${slug.replace(/-/g, "_")}`;

  const eyebrow =
    variant === "credentialing"
      ? "Also across The Shrink Network"
      : "Also on Shrinkopedia";

  const lede =
    variant === "credentialing"
      ? "The same credentialing view, from the encyclopedia side:"
      : entries.length === 1
        ? "I cover the reference version of this on Shrinkopedia:"
        : "I cover the reference versions of this on Shrinkopedia:";

  return (
    <aside
      role="complementary"
      aria-label={eyebrow}
      className={`also-on-shrinkopedia not-prose border-l-2 border-accent/40 pl-5 py-2 ${className ?? ""}`}
    >
      <p className="m-0 text-xs font-bold tracking-[0.2em] uppercase text-accent">
        {eyebrow}
      </p>
      <p className="mt-3 mb-0 text-sm md:text-base font-light leading-relaxed text-secondary">
        {lede}{" "}
        {entries.map((entry, i) => (
          <span key={entry.path}>
            <a
              href={shrinkopediaUrl(entry.path, campaign)}
              target="_blank"
              rel="noopener noreferrer"
              className="underline decoration-accent/40 underline-offset-4 hover:decoration-accent transition-colors"
            >
              {entry.label}
            </a>
            {i < entries.length - 2 ? ", " : i === entries.length - 2 ? ", and " : "."}
          </span>
        ))}
      </p>
    </aside>
  );
}
